import { IconAssigned } from '@components/icons/assigned';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';

import { useAuthenticatedUser } from 'features/authentication';
import { toSortedUserOptions } from 'features/filters/user-options';
import { useStore } from 'models/Provider';

const assigneeFieldToExternalQuery = (filter: FilterData) => {
  switch (filter.comparer) {
    case 'is':
      if (filter.value === 'anyone') return { assigned_to__isnull: false };
      if (filter.value === 'none') return { assigned_to__isnull: true };
      return { assigned_to__term: filter.value };
    case 'any_of':
      return {
        assigned_to__terms: Array.isArray(filter.value) ? filter.value.join('__') : filter.value,
      };
    case 'not_is':
      return { assigned_to__exclude: filter.value };
    case 'none_of':
      return { assigned_to__exclude: filter.value };
    default:
      return {};
  }
};

export const useAssigneeFilter = (): FilterConfigField => {
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();

  return {
    id: 'assignee',
    label: t('applicants.filters.assigned.title'),
    icon: <IconAssigned />,
    type: 'user',
    values: (filter) => {
      const users = toSortedUserOptions(userStore.list);

      if (filter.comparer === 'is') {
        return [
          { value: 'none', label: t('applicants.filters.assigned.none') },
          { value: 'anyone', label: t('applicants.filters.assigned.anyone') },
          ...users,
        ];
      }

      return users;
    },
    quick: [
      {
        id: 'me',
        label: t('applicants.filters.assigned.me'),
        apply: { comparer: 'is', value: String(me.id) },
      },
      {
        id: 'none',
        label: t('applicants.filters.assigned.none'),
        apply: { comparer: 'is', value: 'none' },
      },
      {
        id: 'anyone',
        label: t('applicants.filters.assigned.anyone'),
        apply: { comparer: 'is', value: 'anyone' },
      },
    ],
    allowedComparers: ['is', 'not_is', 'any_of', 'none_of'],
    toExternalQuery: assigneeFieldToExternalQuery,
  };
};
